import { useEffect, useRef, useState } from 'react';
import { useLocalStorage } from 'react-use';

import { Banner } from '@zen/DesignSystem';
import useAccount from '@zen/utils/hooks/useAccount';
import { useNotification } from '@zen/utils/hooks/useNotification';
import useTracking from '@zen/utils/hooks/useTracking';
import type { IOkOrErrorResult } from '@zen/utils/OkOrErrorResult';

import NetPromoterScoreSurvey from './NetPromoterScoreSurvey';
import { questions } from './NetPromoterScoreSurvey/helpers';
import type { NetPromoterScoreSurveyValues } from './NetPromoterScoreSurvey/types';

// Constants
const API_BASE_URL = 'https://gbaaksrkoizcxnaknjzq.supabase.co/functions/v1';

const NetPromoterScoreBanner = () => {
  const effectMounted = useRef<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isBannerVisible, setIsBannerVisible] = useState<boolean>(false);
  const { addSuccess, addError } = useNotification();
  const { trackEvent } = useTracking();
  const { userProfile } = useAccount();
  const [isNpsSurveySkipped, setIsNpsSurveySkipped] = useLocalStorage<boolean>('nps2026SurveySkipped', false);
  const userName = `${userProfile.firstName} ${userProfile.lastName}`;

  useEffect(() => {
    if (effectMounted.current) {
      return;
    }
    effectMounted.current = true;

    const hasUserCompletedSurvey = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/has-customer-voted`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ userId: userProfile.uuid })
        });

        if (!res.ok) {
          throw new Error('Failed to fetch survey status');
        }
        const data = await res.json();

        if (data.hasSubmitted === false) {
          setIsBannerVisible(true);

          if (isNpsSurveySkipped) return;

          setIsOpen(true);
        }
      } catch {
        // Silently handle error - survey can still be shown if needed
      }
    };

    hasUserCompletedSurvey();
  }, [userProfile.uuid]);

  // Pre-compute the results mapping to avoid recreating it on every submit
  const getSurveyResults = (values: NetPromoterScoreSurveyValues) =>
    questions.map(({ name, question }) => ({
      question,
      answer: values[name as keyof NetPromoterScoreSurveyValues]
    }));

  const handleSubmit = async (values: NetPromoterScoreSurveyValues): Promise<IOkOrErrorResult> => {
    try {
      const response = await fetch(`${API_BASE_URL}/submit-survey`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          email: userProfile.email,
          userId: userProfile.uuid,
          userName,
          accountName: userProfile.registeredCompanyName,
          results: getSurveyResults(values)
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit survey');
      }

      return {
        error: null,
        ok: {}
      };
    } catch (err) {
      addError('Failed to submit survey');

      return {
        error: err as Error,
        ok: null
      };
    } finally {
      setIsOpen(false);
    }
  };

  const handleClose = (): void => {
    setIsOpen(false);
    setIsNpsSurveySkipped(true);

    trackEvent({
      category: 'NetPromoterScoreSurvey',
      action: 'SurveyDismissed',
      label: userProfile.email,
      properties: {
        email: userProfile.email,
        userId: userProfile.uuid,
        userName
      }
    });
  };

  const handleSuccess = (): void => {
    addSuccess('Thank you for your feedback!');
    setIsBannerVisible(false);
  };

  return (
    <>
      {isBannerVisible && (
        <Banner
          className="p-4"
          message={
            <div className="flex items-center space-x-2">
              <span>We'd love your feedback! Let us know how we're performing.</span>
              <span className="cursor-pointer underline hover:no-underline" onClick={() => setIsOpen(true)}>
                Provide feedback
              </span>
            </div>
          }
          type="announcement"
        />
      )}
      <NetPromoterScoreSurvey isOpen={isOpen} onClose={handleClose} onSubmit={handleSubmit} onSuccess={handleSuccess} />
    </>
  );
};

export default NetPromoterScoreBanner;
